<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import 'swiper/css/bundle';
import {
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import HotelCard from '@components/util/hotel-card';
import type HotelPackage from '@itinerary/hotel/HotelPackage';
import { MrButton } from '#components';

const {
    hideButtons,
    isLoading,
    items,
    maxWidth = 1200,
    haveLoading,
    mobileOffsetAround = 20,
    noMargin,
    reserveEventName = '',
    mobileDisableSwiper,
} = defineProps<IProps>();
interface IProps {
    items: HotelPackage[];
    hideButtons?: boolean;
    maxWidth?: number;
    noMargin?: boolean;
    haveLoading?: boolean;
    isLoading?: boolean;
    reserveEventName?: string;
    mobileOffsetAround?: number;
    mobileDisableSwiper?: boolean;
    isStatic?: boolean;
    singleImg?: boolean;
}
const next = ref<InstanceType<typeof MrButton>>();
const prev = ref<InstanceType<typeof MrButton>>();
const gtm = useGtm();
const { isMobile } = useSize();
const cItems = computed(() => {
    if (haveLoading && isLoading) {
        return [null, null, null, null] as null[];
    } else {
        return items;
    }
});
const reserve = () => {
    if (reserveEventName) {
        gtm?.trackEvent({
            event: reserveEventName,
        });
    }
};

const navigation = computed(() => ({
    nextEl: next.value?.native,
    prevEl: prev.value?.native,
}));
</script>

<template>
    <div
        class="hotel-blocks-container"
        :style="{ '--max-width': `${maxWidth}px` }"
        :class="{ 'mobile-no-swiper': mobileDisableSwiper }"
    >
        <mr-button
            v-show="!hideButtons"
            ref="prev"
            size="small"
            class="hotel-carousel-prev"
            :icon-button="faChevronRight"
            variant="transparent"
            @click.prevent.stop
        />
        <div
            class="banner-carousel-container"
            :class="{ 'no-margin': noMargin }"
        >
            <component
                :is="mobileDisableSwiper && isMobile ? 'div' : Swiper"
                :key="!!prev?.native"
                :space-between="16"
                :slides-offset-after="mobileOffsetAround"
                :slides-offset-before="mobileOffsetAround"
                slides-per-view="auto"
                free-mode
                observer
                observe-slide-children
                :breakpoints="{
                    1080: {
                        slidesOffsetAfter: 30,
                        slidesOffsetBefore: 4,
                    },
                }"
                :modules="[Navigation]"
                :navigation
                class="swiper-wrapper"
            >
                <SwiperSlide
                    v-for="(card, i) in cItems"
                    :key="card ? card.key : i"
                    class="swiper-slide"
                >
                    <HotelCard
                        :hotel-item="card || undefined"
                        :loading="card === null"
                        mobile-view
                        :is-static
                        single-img
                        @reserve="reserve"
                    />
                </SwiperSlide>
            </component>
        </div>
        <mr-button
            v-show="!hideButtons"
            ref="next"
            size="small"
            :icon-button="faChevronLeft"
            class="hotel-carousel-next"
            variant="transparent"
            @click.prevent.stop
        />
    </div>
</template>

<style lang="scss" scoped>
.hotel-blocks-container {
    display: flex;
    align-items: center;
    max-width: var(--max-width);
    width: 100%;
    margin: 0 auto;
    .hotel-carousel-prev,
    .hotel-carousel-next {
        width: 32px !important;
        border-radius: 4px !important;
        @include mobile-only(1080px) {
            display: none;
        }
        :deep() {
            .mrbilit-icon {
                margin-left: 0;
                font-size: 12px;
            }
        }
    }
    .banner-carousel-container {
        position: relative;
        width: 100%;
        overflow-x: hidden;
        margin-left: 16px;
        margin-right: 16px;
        padding-bottom: 2px;
        @include mobile-only(768px) {
            margin-left: 0;
            margin-right: 0;
        }
        &.no-margin {
            margin-inline: 0;
        }
        .swiper-wrapper {
            padding-bottom: 8px;
            align-items: stretch;
            .swiper-slide {
                flex-basis: 320px;
                max-width: 320px;
                height: unset !important;
                > div {
                    height: 100%;
                }
                @include mobile-only {
                    position: relative;
                }
            }
        }
    }

    &.mobile-no-swiper {
        .swiper-wrapper {
            gap: 8px;
        }
        @include mobile-only {
            .hotel-carousel-next,
            .hotel-carousel-prev {
                display: none;
            }
            .swiper-wrapper {
                display: flex;
                flex-direction: column;
                gap: 16px;
                align-items: stretch;
                padding: 8px 16px;
                .swiper-slide {
                    flex-basis: unset;
                    max-width: calc(100% - 32px);
                    height: unset !important;
                    > div {
                        height: unset;
                    }
                }
            }
        }
    }
}
</style>
